// libs
import React, { memo, useEffect } from "react";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import Helmet from "react-helmet";

// analytics
import DTM from "shared/helpers/DTM";

// global components
import GDPR from "shared/components/GDPR";

// components
import InitialCalls from "./InitialCalls";
import CheckoutURI from "shared/components/CheckoutURI";

// redux
import {
    updateCountry as _updateCountry,
    updateCurrency as _updateCurrency,
} from "shared/store/internationalization/actions";
import { setWebAppType as _setWebAppType } from "shared/store/app/actions";

// helpers
import { getCookie, isScriptLoaded } from "shared/helpers/utils";
import onWindowLoad from "shared/helpers/dom/onWindowLoad";
import { getWebAppType } from "./helpers";

// defs
interface IBootProps {
    fetchUser: () => any;
    isLoggedInUser: boolean;
    userEmail: string;
    selectedCurrency: string;
    selectedCountryCode: string;
    selectedCountryName: string;
    currencyDisplayValue: string;
    firstName: string;
    lastName: string;
    updateCountry: typeof _updateCountry;
    updateCurrency: typeof _updateCurrency;
    isI18nLoading: boolean;
    currencies: {
        currencyCode: string;
        currencyDisplayValue: string;
    }[];
    countries: {
        value: string;
        name: string;
    }[];
    fetchOffer: () => void;
    setDefaultCookies: (body: { [key: string]: string }) => void;
    isXHRAllowed: boolean;
    onGDPRNetwork: boolean;
    dnsLinks: string;
    setWebAppType: typeof _setWebAppType;
}

/**
 * Use this component to execute on Browser side - On Boot
 *
 * NOTE: Only execute scripts which impact globally
 * e.g - Do API calls to update redux - which would be used by multiple components on client
 */
function Boot({
    fetchUser,
    isLoggedInUser,
    userEmail,
    updateCountry,
    updateCurrency,
    isI18nLoading,
    currencies,
    countries,
    selectedCountryCode,
    selectedCountryName,
    selectedCurrency,
    currencyDisplayValue,
    firstName,
    lastName,
    fetchOffer,
    setDefaultCookies,
    isXHRAllowed,
    onGDPRNetwork,
    dnsLinks,
    setWebAppType,
}: IBootProps) {
    // Update DTM user logged In status
    useEffect(() => {
        DTM.isLoggedIn = isLoggedInUser;
    }, [isLoggedInUser]);

    // Update DTM user email
    useEffect(() => {
        try {
            DTM.setUserData(userEmail, firstName, lastName);
        } catch (err) {
            /** DO NOTHING */
        }
    }, [userEmail, firstName, lastName]);

    // Update currency
    useEffect(() => {
        if (isI18nLoading || isEmpty(currencies) || isEmpty(countries)) {
            return;
        }

        const _countryCode = getCookie("countryName") || selectedCountryCode;
        const _countryName = get(
            find(countries, country => country.value === _countryCode),
            "name",
            "India"
        );
        const flagUrl = get(
            find(countries, country => country.value === _countryCode),
            "flagUrl",
            ""
        );

        const _currency = getCookie("CurrencyCode") || selectedCurrency;
        const _currencyDisplayValue = get(
            find(currencies, currency => currency.currencyCode === _currency),
            "currencyDisplayValue",
            currencyDisplayValue
        );

        updateCountry({
            countryCode: _countryCode,
            country: _countryName,
            flagUrl: flagUrl,
        });

        updateCurrency({
            currency: _currency,
            currencyDisplayValue: _currencyDisplayValue,
        });
    }, [
        isI18nLoading,
        countries,
        currencies,
        selectedCountryCode,
        selectedCurrency,
        currencyDisplayValue,
        updateCountry,
        updateCurrency,
    ]);

    useEffect(() => {
        if (isXHRAllowed === false) {
            return;
        }

        const SRC = `${process.env.DTM_URL}`;

        if (isScriptLoaded(SRC)) {
            return;
        }

        // defer loading DTM
        onWindowLoad(() => {
            // @ts-ignore
            window.requestIdleCallback(() => {
                const script = document.createElement("script");
                script.defer = true;
                script.onload = () => {
                    // Do Something on script load
                };
                script.src = SRC;
                document.body.appendChild(script);
            });
        });
    }, [isXHRAllowed]);

    useEffect(() => {
        setWebAppType(getWebAppType());
    }, [setWebAppType]);

    const HeadElements = () => {
        let linkTags: React.ReactElement[] = [];
        try {
            const links = JSON.parse(dnsLinks);

            const preConnectLinks = links["preConnect"].map((item: string, indx: number) => (
                <link rel="preconnect" href={item} key={"preConnect_" + indx} />
            ));
            const dnsLookupLinks = links["preConnect"].map((item: string, indx: number) => (
                <link rel="dns-prefetch" href={item} key={"preConnect_prefetch_" + indx} />
            ));
            const dnsLookupOnlyLinks = links["dnsOnly"].map((item: string, indx: number) => (
                <link rel="dns-prefetch" href={item} key={"prefetch_" + indx} />
            ));
            linkTags = [...preConnectLinks, ...dnsLookupLinks, ...dnsLookupOnlyLinks];
        } catch (e) {
            //Do Nothing
        }
        return <Helmet>{linkTags}</Helmet>;
    };

    return (
        <>
            <HeadElements />
            {onGDPRNetwork && <GDPR />}

            {isXHRAllowed && (
                <>
                    <InitialCalls
                        selectedCurrency={selectedCurrency}
                        selectedCountryCode={selectedCountryCode}
                        selectedCountryName={selectedCountryName}
                        fetchUser={fetchUser}
                        fetchOffer={fetchOffer}
                        setDefaultCookies={setDefaultCookies}
                    />
                    <CheckoutURI />
                </>
            )}
        </>
    );
}

export default memo(Boot);
